import { useCallback, useState } from "react";
import { useGomakeAxios } from "@/hooks";
import { useSnackBar } from "@/hooks/use-snack-bar";
import { sendProofJobToProductionApi } from "@/services/api-service/production-floor/production-floor-endpoints";

export interface SendProofJobToProductionOptions {
  keepInSameStation?: boolean;
  onSuccess?: () => void;
}

const useSendProofJobToProduction = (defaultOnSuccess?: () => void) => {
  const { callApi } = useGomakeAxios();
  const { alertSuccessUpdate, alertFaultUpdate, alertFault } = useSnackBar();
  const [isSending, setIsSending] = useState(false);

  const sendProofJobToProduction = useCallback(
    (
      boardMissionId: string,
      options?: SendProofJobToProductionOptions
    ): Promise<boolean> => {
      if (!boardMissionId) {
        return Promise.resolve(false);
      }

      setIsSending(true);

      return new Promise((resolve) => {
        sendProofJobToProductionApi(
          callApi,
          (res) => {
            setIsSending(false);
            if (res?.success) {
              alertSuccessUpdate();
              (options?.onSuccess ?? defaultOnSuccess)?.();
              resolve(true);
              return;
            }

            const errorMessage =
              res?.data?.ErrorMessage ||
              res?.data?.message ||
              (res as { message?: string })?.message ||
              (res as { msg?: string })?.msg;

            if (errorMessage) {
              alertFault(errorMessage);
            } else {
              alertFaultUpdate();
            }
            resolve(false);
          },
          {
            boardMissionId,
            keepInSameStation: options?.keepInSameStation ?? false,
          }
        ).catch(() => {
          setIsSending(false);
          alertFaultUpdate();
          resolve(false);
        });
      });
    },
    [
      callApi,
      alertSuccessUpdate,
      alertFaultUpdate,
      alertFault,
      defaultOnSuccess,
    ]
  );

  return { sendProofJobToProduction, isSending };
};

export { useSendProofJobToProduction };
